<script setup lang="ts">
import type { Component, Ref } from 'vue';
import type { FieldTypeFilterConfig } from '#layers/data-model/public';
import type { SegmentedFieldFilter } from './utils/filter-path';
import type { FilterSchemaPrimitiveField } from './utils/filter-schema';
import { cloneDeep, set } from 'lodash-es';
import { computed, onMounted, ref, resolveComponent, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from '#layers/base/public';
import { fromCurrentUserFieldVariable, injectFilterInputContext, isBetweenValueOperator, isCurrentUserVariable, isListValueOperator, isPrimitiveTypeDecimal, isPrimitiveTypeNumber, isSingleValueOperator, provideFilterInputContext } from '#layers/data-model/public';
import FilterInputComponent from './FilterInputComponent.vue';
import { injectFilterInputGroupDelegateContext, provideFilterInputGroupDelegateContext } from './FilterInputGroupDelegateContext';

const {
	primitiveField,
	filterConfig,
	typeOptions,
	disabled,
	readonly,
	enableVariables,
} = defineProps<{
	primitiveField: FilterSchemaPrimitiveField;
	filterConfig?: FieldTypeFilterConfig;
	typeOptions?: Record<string, unknown>;
	disabled?: boolean;
	readonly?: boolean;
	enableVariables?: boolean;
}>();

const emit = defineEmits<{
	remove: [];
}>();

const model = defineModel<SegmentedFieldFilter | null>({ required: true });

// Check if we're being rendered as a delegate (private context already provided)
const delegateContext = injectFilterInputGroupDelegateContext(null);
const isDelegate = delegateContext !== null;

// When delegating, read from private context instead of props
const resolvedModel = isDelegate ? delegateContext!.model : model;
const resolvedPrimitiveField = computed(() => isDelegate ? delegateContext!.primitiveField.value : primitiveField);
const resolvedFilterConfig = computed(() => isDelegate ? delegateContext!.filterConfig.value : filterConfig);
const resolvedTypeOptions = computed(() => isDelegate ? delegateContext!.typeOptions.value : typeOptions);
const resolvedDisabled = computed(() => isDelegate ? delegateContext!.disabled.value : disabled);
const resolvedReadonly = computed(() => isDelegate ? delegateContext!.readonly.value : readonly);
const isEditable = computed(() => !resolvedReadonly.value && !resolvedDisabled.value);

// Resolve the leaf input component
const existingPublicContext = injectFilterInputContext(null);

const resolvedFilterInput = computed<Component>(() => {
	if (isDelegate && existingPublicContext) return existingPublicContext.FilterInput;
	return filterConfig?.input ?? FilterInputComponent;
});

// Only provide contexts when we're the primary (not a delegate)
if (!isDelegate) {
	provideFilterInputContext({
		get FilterInput() {
			return resolvedFilterInput.value;
		},
		FilterInputGroup: resolveComponent('FilterInputGroup') as Component,
	});

	provideFilterInputGroupDelegateContext({
		primitiveField: computed(() => primitiveField),
		filterConfig: computed(() => filterConfig),
		typeOptions: computed(() => typeOptions ?? {}),
		model: model as Ref<SegmentedFieldFilter | null>,
		disabled: computed(() => disabled ?? false),
		readonly: computed(() => readonly ?? false),
	});
}

const { t } = useI18n();
interface FocusableFilterInput {
	focusFirstInput?: () => void;
}

const filterInputRef = useTemplateRef<FocusableFilterInput | FocusableFilterInput[]>('filterInputRef');
const customInputGroupRef = useTemplateRef<FocusableFilterInput>('customInputGroupRef');
const { inputsContainerRef, handleKeydown, focusFirstInput: focusFirstNativeInput } = useArrowNavigation();
const pendingFocus = ref(false);

function focusFirstInput() {
	if (!isDelegate && resolvedFilterConfig.value?.inputGroup) {
		if (customInputGroupRef.value?.focusFirstInput) {
			pendingFocus.value = false;
			customInputGroupRef.value.focusFirstInput();
			return;
		}

		pendingFocus.value = true;
		return;
	}

	const firstFilterInput = Array.isArray(filterInputRef.value) ? filterInputRef.value[0] : filterInputRef.value;

	if (firstFilterInput?.focusFirstInput) {
		pendingFocus.value = false;
		firstFilterInput.focusFirstInput();
		return;
	}

	if (inputsContainerRef.value?.querySelector('input')) {
		pendingFocus.value = false;
		focusFirstNativeInput();
		return;
	}

	pendingFocus.value = true;
}

watch([filterInputRef, customInputGroupRef], () => {
	if (pendingFocus.value) focusFirstInput();
});

defineExpose({ focusFirstInput });

const operator = computed(() => resolvedModel.value?.operator);
const isSingleOperatorValue = computed(() => isSingleValueOperator(operator.value));
const isListOperatorValue = computed(() => isListValueOperator(operator.value));
const isBetweenOperatorValue = computed(() => isBetweenValueOperator(operator.value));
const fieldType = computed(() => resolvedPrimitiveField.value.type);

const isNumericField = computed(() => isPrimitiveTypeNumber(fieldType.value) || isPrimitiveTypeDecimal(fieldType.value));

const value = computed<any | any[]>({
	get() {
		if (!resolvedModel.value) return;
		// Re-derive from serialized form for _in/_nin sentinel handling
		const raw = resolvedModel.value.value;
		if (!isListOperatorValue.value) return raw;
		// A to-many $CURRENT_USER variable is a single token that resolves to the whole list,
		// so it is stored as a bare string rather than an array of entered values.
		if (isVariableEntry(raw)) return raw;
		const entries = Array.isArray(raw) ? raw : (isNil(raw) ? [] : [raw]);
		const nonEmptyEntries = entries.filter((val) => val !== '');
		return [...(nonEmptyEntries.some((val) => !isNil(val)) ? nonEmptyEntries : []), null];
	},
	set(newVal) {
		if (!isEditable.value) return;
		if (!resolvedModel.value) return;
		resolvedModel.value = { ...resolvedModel.value, value: toOperandValue(newVal) };
	},
});

const operandValues = computed<unknown[]>(() => {
	if (Array.isArray(value.value)) return value.value;
	return isBetweenOperatorValue.value ? [undefined, null] : [];
});

// A whole-value variable (single, or a to-many stored as one token) collapses to the bare string in `value`,
// so this one flag covers both and renders a single pill instead of any field input.
const isVariableValue = computed(() => isVariableEntry(value.value));
const currentUserFieldPath = computed(() => fromCurrentUserFieldVariable(value.value));

function onRemoveVariable() {
	value.value = undefined;
	void focusFirstInput();
}

function isVariableEntry(entry: unknown) {
	return Boolean(enableVariables) && isCurrentUserVariable(entry);
}

/**
 * Normalizes an edited operand for the current operator. For `_in`/`_nin` an emptied list becomes
 * `undefined` rather than `[]`: `[]` is a complete "in the empty set" filter that matches nothing,
 * while a row the user has cleared is still being authored and has to stay prunable.
 */
function toOperandValue(newVal: any) {
	if (!isListOperatorValue.value) return newVal;
	if (isNil(newVal)) return;

	const entries = [...(newVal as (string | number | null | undefined)[])];
	// The getter appends exactly one blank input for adding the next value; remove only that sentinel.
	if (isNil(entries.at(-1))) entries.pop();

	const normalizedEntries = entries
		.flatMap<string | number | null | undefined>((val) => (typeof val === 'string' ? val.split(',') : [val]))
		.filter((val) => val !== '');

	return normalizedEntries.some((val) => !isNil(val)) ? normalizedEntries : undefined;
}

function setValueAt(index: number, newValue: any) {
	if (!isEditable.value) return;
	value.value = set(cloneDeep(operandValues.value), index, newValue);
}

function removeValueAt(index: number) {
	if (!isEditable.value) return;
	const next = [...(value.value as unknown[])];
	next.splice(index, 1);
	value.value = next;
	void focusFirstInput();
}

const customInputGroupModel = computed(() =>
	isEditable.value ? model.value : cloneDeep(model.value),
);

function updateCustomInputGroupModel(value: SegmentedFieldFilter | null) {
	if (!isEditable.value) return;
	model.value = value;
}

function removeCustomInputGroup() {
	if (!isEditable.value) return;
	emit('remove');
}

function useArrowNavigation() {
	const inputsContainerRef = useTemplateRef<HTMLElement>('inputsContainerRef');

	function getFilterInputs(): HTMLInputElement[] {
		if (!inputsContainerRef.value) return [];
		return [...inputsContainerRef.value.querySelectorAll('input')];
	}

	function focusInputAt(index: number) {
		const inputs = getFilterInputs();

		if (inputs[index]) {
			inputs[index].focus();
		}
	}

	function getCurrentInputIndex(): number {
		const inputs = getFilterInputs();
		const activeElement = document.activeElement;
		return [...inputs].indexOf(activeElement as HTMLInputElement);
	}

	function focusNextInput() {
		const currentIndex = getCurrentInputIndex();
		focusInputAt(currentIndex + 1);
	}

	function focusPreviousInput() {
		const currentIndex = getCurrentInputIndex();
		focusInputAt(currentIndex - 1);
	}

	function focusFirstInput() {
		focusInputAt(0);
	}

	function focusLastInput() {
		const inputs = getFilterInputs();
		focusInputAt(inputs.length - 1);
	}

	function handleKeydown(event: KeyboardEvent) {
		const target = event.target as HTMLInputElement;

		switch (event.key) {
			case 'ArrowRight':
				if (target.selectionStart === target.value.length) {
					event.preventDefault();
					focusNextInput();
				}

				break;
			case 'ArrowLeft':
				if (target.selectionEnd === 0) {
					event.preventDefault();
					focusPreviousInput();
				}

				break;
			case 'Home':
				event.preventDefault();
				focusFirstInput();
				break;
			case 'End':
				event.preventDefault();
				focusLastInput();
				break;
			case 'Backspace':
				if (target.value.length === 0) {
					event.preventDefault();
					focusPreviousInput();
				}

				break;
		}
	}

	onMounted(() => {
		// Skip auto-focus when a custom inputGroup handles its own focus
		if (!isDelegate && resolvedFilterConfig.value?.inputGroup) return;
		focusFirstInput();
	});

	return {
		inputsContainerRef,
		handleKeydown,
		focusFirstInput,
	};
}
</script>

<template>
	<div v-if="isVariableValue" class="flex items-center">
		<FilterCurrentUserBadge
			:field-path="currentUserFieldPath"
			:show-remove="isEditable"
			@remove="onRemoveVariable"
		/>
	</div>

	<!-- Custom inputGroup override: render instead of default layout -->
	<component
		:is="resolvedFilterConfig?.inputGroup"
		v-else-if="!isDelegate && resolvedFilterConfig?.inputGroup"
		ref="customInputGroupRef"
		:model-value="customInputGroupModel"
		:primitive-type="fieldType"
		:type-options="resolvedTypeOptions"
		:disabled="resolvedDisabled"
		:readonly="resolvedReadonly"
		@update:model-value="updateCustomInputGroupModel"
		@remove="removeCustomInputGroup"
	/>

	<!-- Default layout (also used when rendered as delegate) -->
	<!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- delegates arrow-key movement between the inner inputs, which are the interactive elements -->
	<div v-else-if="operator" ref="inputsContainerRef" @keydown="handleKeydown">
		<div
			v-if="isSingleOperatorValue"
			class="flex h-full items-center"
		>
			<component
				:is="resolvedFilterInput"
				ref="filterInputRef"
				v-model="value"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
			/>
		</div>
		<div v-else-if="isListOperatorValue" class="flex h-full items-center">
			<template v-for="(val, index) in value" :key="index">
				<FilterCurrentUserBadge
					v-if="isVariableEntry(val)"
					class="not-last:after:me-0.5 not-last:after:text-xs not-last:after:leading-none not-last:after:content-[',']"
					:field-path="fromCurrentUserFieldVariable(val)"
					:show-remove="isEditable"
					@remove="removeValueAt(Number(index))"
				/>
				<component
					:is="resolvedFilterInput"
					v-else
					ref="filterInputRef"
					class="not-last:after:me-0.5 not-last:after:text-xs not-last:after:content-[',']"
					:class="{ 'not-last:[&_input]:pe-0': !isNumericField }"
					:primitive-type="fieldType"
					:type-options="resolvedTypeOptions"
					:model-value="val"
					:disabled="resolvedDisabled"
					:readonly="resolvedReadonly"
					:small="value.length > 1"
					@update:model-value="setValueAt(Number(index), $event)"
				/>
			</template>
		</div>
		<div v-else-if="isBetweenOperatorValue" class="flex h-full items-baseline">
			<component
				:is="resolvedFilterInput"
				ref="filterInputRef"
				data-testid="filter-range-lower"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:model-value="operandValues[0]"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
				small
				@update:model-value="setValueAt(0, $event)"
			/>
			<div class="mx-2 text-xs">
				{{ t('common.and') }}
			</div>
			<component
				:is="resolvedFilterInput"
				ref="filterInputRef"
				data-testid="filter-range-upper"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:model-value="operandValues[1]"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
				small
				@update:model-value="setValueAt(1, $event)"
			/>
		</div>
	</div>
</template>
